import { AlertTriangle, Workflow } from "lucide-react";

import { usePhaseReference, useReplicaPhaseRef } from "@/api/local/phases";
import type { Replica, ReplicaPhase } from "@/api/types";
import { AutoReapPanel } from "@/components/AutoReapPanel";
import { ReplicaPhaseBadge } from "@/components/PhaseBadge";
import { Section } from "@/components/Section";

interface Props {
  replica: Replica;
}

/**
 * Replica-dashboard phase summary.
 *
 * Renders the step-strip progression `QUEUED → PROVISIONING →
 * SETUP → HEALTHY → DRAINING → SHUTTING_DOWN` so the operator can
 * see at a glance where in its lifecycle the replica is. Failed
 * replicas show the failure pin (the strip stalls at the stage we
 * failed in) plus the classified `failure_kind` / `failure_reason`
 * below.
 *
 * Phases off the happy path (`unhealthy`, `failed`, `reaped`) are
 * rendered as branch boxes after the strip rather than slotted
 * into the linear progression — the progress_index is `null` for
 * those by design.
 */
export function ReplicaPhaseCard({ replica }: Props) {
  const ref = useReplicaPhaseRef(replica.phase);
  const all = usePhaseReference();
  // Build the linear happy-path step list from the reference data
  // so the order can't drift between the controller catalogue and
  // the UI. ``progress_index`` is the authoritative key.
  const steps = (all.data?.replica_phases ?? [])
    .filter((p) => p.progress_index !== null)
    .sort(
      (a, b) => (a.progress_index ?? 0) - (b.progress_index ?? 0),
    );
  const activeIndex =
    ref?.progress_index ?? indexFromBranch(replica.phase, steps);
  const isFailed = replica.phase === "failed";
  return (
    <Section title="Lifecycle" icon={<Workflow />} count={ref?.label}>
      <div className="phase-card">
        <div className="phase-card__row">
          <ReplicaPhaseBadge phase={replica.phase} hideHelpLink />
          {ref?.description && (
            <p className="muted phase-card__desc">{ref.description}</p>
          )}
        </div>
        {steps.length > 0 && (
          <div className="phase-strip" aria-label="Replica lifecycle steps">
            {steps.map((s) => {
              const idx = s.progress_index ?? 0;
              let kind: "done" | "active" | "failed" | "pending";
              if (isFailed) {
                kind = idx <= activeIndex ? "failed" : "pending";
              } else if (idx < activeIndex) {
                kind = "done";
              } else if (idx === activeIndex) {
                kind = "active";
              } else {
                kind = "pending";
              }
              return (
                <div
                  key={s.slug}
                  className={`phase-strip__step phase-strip__step--${kind}`}
                  title={`${s.label}: ${s.description}`}
                >
                  {s.label}
                </div>
              );
            })}
          </div>
        )}
        {ref?.progress_index === null && !isFailed && (
          <div className="phase-card__banner phase-card__banner--warn">
            <span>
              Branch state — see the Phases reference for what this
              means.
            </span>
          </div>
        )}
        {replica.failure_kind && (
          <div className="phase-card__banner phase-card__banner--danger">
            <AlertTriangle size={14} />
            <div>
              <div className="phase-card__error-kind">
                {replica.failure_kind}
              </div>
              {replica.failure_reason && (
                <pre className="phase-card__error-body">
                  {replica.failure_reason}
                </pre>
              )}
            </div>
          </div>
        )}
        {replica.auto_reap?.enabled && (
          <AutoReapPanel status={replica.auto_reap} />
        )}
      </div>
    </Section>
  );
}

/**
 * For phases that branch off the happy path (`unhealthy`, `failed`,
 * `reaped`) we still want the step strip to highlight *where* the
 * replica was when it left the path. Approximation: anything that
 * looks "post-setup" pins the active step at `healthy`; otherwise
 * pin at `setup`.
 */
function indexFromBranch(
  phase: ReplicaPhase,
  steps: { progress_index: number | null; slug: ReplicaPhase }[],
): number {
  // SkyPilot's terminal failures most commonly happen during setup
  // (the FAILED_INITIAL_DELAY / FAILED_PROBING family). Default to
  // the `setup` step's index so the strip stalls in a useful place.
  if (phase === "unhealthy" || phase === "reaped") {
    return findIndex(steps, "healthy");
  }
  if (phase === "failed") return findIndex(steps, "setup");
  return 0;
}

function findIndex(
  steps: { progress_index: number | null; slug: ReplicaPhase }[],
  needle: ReplicaPhase,
): number {
  const hit = steps.find((s) => s.slug === needle);
  return hit?.progress_index ?? 0;
}
